import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong, faChevronDown, faChevronUp } from "@fortawesome/pro-light-svg-icons";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { showError, showSuccess } from "redux/actions/ui";
import { StatusCode } from "api/protocols";
import { UserLogout } from "api/rpc";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import "./clientPortalNavigation.scss";
import "./Home/clientPortalHome.scss";
import classNames from "classnames";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import { DESKTOP_WIDTH, SMALL_DESKTOP_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { useClientPortal } from "./useClientPortal";
import { NavLink } from "react-router-dom";
import GoogleAnalytics from "components/googleAnalytics/GoogleAnalytics";
import LoaderCard from "components/loaderCard/LoaderCard";
import { ButtonNew } from "components/buttonNew";
import { t } from "i18next";
import { PutCustomerPreferences } from "api/rpc/2024-04/customer/customer";
import StatusBannerStack from "components/statusBanner/StatusBannerStack";

interface IParams {
  clientShortName: string;
}

interface IState {
  profileDetailsOpen: boolean;
  clientDetailsOpen: boolean;
}

interface IProps {
  showNavOptions?: boolean;
}

export function ClientPortalNavigation(props: IProps) {
  const { t } = useTranslation();
  const { showNavOptions } = props;
  const dispatch = useAppDispatch();
  const { clientShortName } = useParams<IParams>();
  const clientPortalStore = useAppSelector(store => store.clientPortalStore);
  const history = useHistory();
  const profileContainer = useRef<HTMLDivElement>();
  const clientContainer = useRef<HTMLDivElement>();
  const windowSize = useWindowSize();
  const HOME_URL = useClientPortal().getHomePageUrl();

  const [state, setState] = useState<IState>({
    profileDetailsOpen: false,
    clientDetailsOpen: false,
  });

  function sendToNextPage(address: string) {
    history.push(address);
  }

  async function logoutUser() {
    const logoutRes = await UserLogout(true);

    if (logoutRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error logging out")));
    }

    window.location.href = HOME_URL;
  }

  function sendToLogin() {
    sendToNextPage(`/portal/${clientShortName}/login`);
  }

  function sendToPage(address: string, openNewTab = false) {
    if (window.location.search.includes(address)) {
      return;
    } else if (openNewTab) {
      window.open(address);
      return;
    }
    history.push(address);
  }

  useOutsideAlerter(profileContainer, () => {
    setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
  });

  useOutsideAlerter(clientContainer, () => {
    setState(prevState => ({ ...prevState, clientDetailsOpen: false }));
  });

  // Show facility logo if client only has one facility
  const displayClientLogo =
    clientPortalStore?.client?.facilities?.length === 1
      ? clientPortalStore?.client?.facilities[0]?.logo_source
      : clientPortalStore?.client?.logo_source_url;

  return (
    <>
      <StatusBannerStack surface="customer_portal" />
      <div className="client-portal-navigation">
      {/* <MetaPixel pixelID={clientPortalStore?.selectedFacility?.facebook_pixel_code ?? undefined} /> */}
      <GoogleAnalytics
        title={
          clientPortalStore?.client?.long_name ? `${clientPortalStore?.client?.long_name} Portal` : "Tee On Portal"
        }
        tagID={clientPortalStore?.selectedFacility?.google_analytics_code ?? undefined}
      />
      <div className="parent-container">
        <div className="options-container">
          {windowSize.width <= DESKTOP_WIDTH && (state.clientDetailsOpen || state.profileDetailsOpen) && (
            <div
              className="client-dropdown-backdrop"
              onClick={() =>
                setState(prevState => ({ ...prevState, clientDetailsOpen: false, profileDetailsOpen: false }))
              }
            ></div>
          )}
          <div
            className="user-info-parent-container client-dropdown-container"
            ref={clientContainer}
            onClick={
              windowSize.width <= DESKTOP_WIDTH && showNavOptions
                ? () => setState(prevState => ({ ...prevState, clientDetailsOpen: true }))
                : undefined
            }
          >
            <div className="client-logo-container">
              {!clientPortalStore?.client?.facilities ? (
                <LoaderCard image borderless />
              ) : (
                <img className="client-logo" src={displayClientLogo} alt={t("Client logo")} />
              )}
            </div>
            {windowSize.width <= SMALL_DESKTOP_WIDTH &&
              showNavOptions &&
              (state.clientDetailsOpen ? (
                <FontAwesomeIcon icon={faChevronUp} />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} />
              ))}
            <div
              className={classNames("profile-dropdown-container", {
                "display-user-info-dropdown": state.clientDetailsOpen,
                "profile-dropdown-container-client": state.clientDetailsOpen,
              })}
            >
              <div className="dropdown-image-container">
                <div className="client-logo-container">
                  <img className="client-logo" src={displayClientLogo} alt={t("Client logo")} />
                </div>
                <FontAwesomeIcon
                  onClick={e => {
                    e.stopPropagation();
                    setState(prevState => ({ ...prevState, clientDetailsOpen: false }));
                  }}
                  icon={faXmark}
                  className="profile-close-button"
                  fixedWidth
                />
              </div>
              <div onClick={() => sendToPage(HOME_URL)} className="profile-action">
                <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "golf-flag-hole"]} />
                <span>{t("Tee Times")}</span>
              </div>

              <div
                onClick={() => sendToPage(`/online-store/${clientPortalStore?.selectedFacility?.short_name}`, true)}
                className="profile-action"
              >
                <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "cart-shopping"]} />
                <span>{t("Store")}</span>
              </div>

              {clientPortalStore?.user && (
                <>
                  <div
                    onClick={() => sendToPage(`/portal/${clientShortName}/bookings-list`)}
                    className="profile-action"
                  >
                    <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "calendar"]} />
                    <span>{t("Bookings")}</span>
                  </div>
                  <div onClick={() => sendToPage(`/portal/${clientShortName}/memberships`)} className="profile-action">
                    <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "id-card-clip"]} />
                    <span>{t("Memberships")}</span>
                  </div>
                </>
              )}
              <div onClick={() => sendToPage(`/portal/${clientShortName}/events`)} className="profile-action">
                <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "trophy"]} />
                <span>{t("Events")}</span>
              </div>
              <div onClick={() => sendToPage(`/portal/${clientShortName}/calendar`)} className="profile-action">
                <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "calendars"]} />
                <span>{t("Calendar")}</span>
              </div>
              {!!clientPortalStore?.selectedFacility?.module_reservations && (
                <div onClick={() => sendToPage(`/portal/${clientShortName}/reservations`)} className="profile-action">
                  <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "calendar"]} />
                  <span>{t("Reservations")}</span>
                </div>
              )}
              {!!clientPortalStore?.selectedFacility?.module_food_ordering && (
                <div
                  onClick={() => sendToPage(`/portal/${clientShortName}/food-ordering/menu`)}
                  className="profile-action"
                >
                  <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "burger-soda"]} />
                  <span>{t("Food Ordering")}</span>
                </div>
              )}

              {!!clientPortalStore?.selectedFacility?.module_accommodations && (
                <div
                  onClick={() =>
                    sendToPage(
                      `/portal/${clientShortName}/accommodations/${clientPortalStore?.selectedFacility?.short_name ?? ""
                      }`,
                    )
                  }
                  className="profile-action"
                >
                  <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "hotel"]} />
                  <span>{t("Accommodations")}</span>
                </div>
              )}
              <div onClick={() => sendToPage(`/portal/${clientShortName}/newsletter`)} className="profile-action">
                <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", "envelope-open-text"]} />
                <span>{t("Newsletter")}</span>
              </div>
            </div>
          </div>
          <div
            className={classNames("options-container options-bar", {
              "options-hide": !showNavOptions || windowSize.width <= SMALL_DESKTOP_WIDTH,
            })}
          >
            <NavLink to={HOME_URL} activeClassName="option-button-active">
              {t("Tee Times")}
            </NavLink>
            <NavLink
              to={`/online-store/${clientPortalStore?.selectedFacility?.short_name}`}
              activeClassName="option-button-active"
              target="_blank"
            >
              {t("Store")}
            </NavLink>
            {clientPortalStore?.user && (
              <>
                <NavLink to={`/portal/${clientShortName}/bookings-list`} activeClassName="option-button-active">
                  {t("Bookings")}
                </NavLink>
                <NavLink to={`/portal/${clientShortName}/memberships`} activeClassName="option-button-active">
                  {t("Memberships")}
                </NavLink>
              </>
            )}
            <NavLink to={`/portal/${clientShortName}/events`} activeClassName="option-button-active">
              {t("Events")}
            </NavLink>
            <NavLink to={`/portal/${clientShortName}/calendar`} activeClassName="option-button-active">
              {t("Calendar")}
            </NavLink>
            {!!clientPortalStore?.selectedFacility?.module_reservations && (
              <NavLink
                to={`/portal/${clientShortName}/reservations/${clientPortalStore?.selectedFacility?.short_name ?? ""}`}
                activeClassName="option-button-active"
              >
                {t("Reservations")}
              </NavLink>
            )}
            {!!clientPortalStore?.selectedFacility?.module_food_ordering && (
              <NavLink to={`/portal/${clientShortName}/food-ordering/menu`} activeClassName="option-button-active">
                {t("Food Ordering")}
              </NavLink>
            )}

            {!!clientPortalStore?.selectedFacility?.module_accommodations && (
              <NavLink
                to={`/portal/${clientShortName}/accommodations/${clientPortalStore?.selectedFacility?.short_name ?? ""
                  }`}
                activeClassName="option-button-active"
              >
                {t("Accommodations")}
              </NavLink>
            )}
            <NavLink to={`/portal/${clientShortName}/newsletter`} activeClassName="option-button-active">
              {t("Newsletter")}
            </NavLink>
          </div>
        </div>

        {clientPortalStore?.user ? (<>
          <div className="ml-auto">
            <SubscribeEmailMarketingBtn />
          </div>
          <div
            className="user-info-parent-container"
            ref={profileContainer}
            onClick={() => setState(prevState => ({ ...prevState, profileDetailsOpen: true }))}
          >

            <p className="user-info-name">
              Hi, {clientPortalStore?.user?.first_name}{" "}
              {clientPortalStore?.user?.last_name
                ? `${clientPortalStore?.user?.last_name?.charAt(0)?.toUpperCase()}.`
                : ""}
            </p>
            <div className="profile-image-container">
              {clientPortalStore?.user?.profile_image_source ? (
                <img
                  className="profile-image"
                  src={clientPortalStore?.user?.profile_image_source}
                  alt={t("User profile image")}
                />
              ) : (
                <div className="client-portal-profile-default-image">
                  <p>
                    {clientPortalStore?.user?.first_name?.charAt(0)?.toUpperCase()}
                    {clientPortalStore?.user?.last_name?.charAt(0)?.toUpperCase()}
                  </p>
                </div>
              )}
            </div>

            <div
              className={classNames("profile-dropdown-container", {
                "display-user-info-dropdown": state.profileDetailsOpen,
              })}
            >
              <FontAwesomeIcon
                onClick={e => {
                  e.stopPropagation();
                  setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
                }}
                icon={faXmark}
                className="profile-close-button"
              />
              <div className="profile-user-info-container">
                <div className="profile-image-container">
                  {clientPortalStore?.user?.profile_image_source ? (
                    <img
                      className="profile-image"
                      src={clientPortalStore?.user?.profile_image_source}
                      alt={t("User profile image")}
                    />
                  ) : (
                    <div className="client-portal-profile-default-image">
                      <p>
                        {clientPortalStore?.user?.first_name?.charAt(0)?.toUpperCase()}
                        {clientPortalStore?.user?.last_name?.charAt(0)?.toUpperCase()}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <p className="profile-user-name">Hello, {`${clientPortalStore?.user?.full_name}`}</p>
                  <p className="profile-user-email">{clientPortalStore?.user?.email}</p>
                </div>
              </div>
              <div
                className="profile-actions-container"
                onClick={e => {
                  e.stopPropagation();
                  setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
                }}
              >
                <div onClick={() => sendToPage("/tee-on/portal/overview", true)} className="profile-action">
                  <FontAwesomeIcon className="profile-action-icon" icon={["far", "user"]} />
                  <span>{t("Account Overview")}</span>
                </div>
                <div onClick={() => void logoutUser()} className="profile-action">
                  <FontAwesomeIcon className="profile-action-icon" icon={["far", "right-from-bracket"]} />
                  <span>{t("Logout")}</span>
                </div>
              </div>
            </div>
          </div>
        </>) : (
          <div onClick={sendToLogin}>
            <p className="client-portal-login-button">
              Login <FontAwesomeIcon icon={faArrowRightLong} />
            </p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

function SubscribeEmailMarketingBtn() {
  const clientPortalStore = useAppSelector(store => store.clientPortalStore);
  const dispatch = useAppDispatch();

  const permissions = clientPortalStore.selectedFacility.permissions;

  async function updateMarketingPreference() {
    const resp = await PutCustomerPreferences(
      {
        client_id: clientPortalStore.client.id,
        marketing_email: true,
      },
      false,
    );

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(t('Failed to subscribe to emails')));
      return;
    }

    dispatch(showSuccess(t('Subscribed to emails')));
  }

  return <>{
    permissions?.email_marketing_allow_opt_ins &&
    permissions?.email_marketing_sign_up_button &&
    <ButtonNew type="secondary" size="xsmall" className="mr-4" onClick={() => void updateMarketingPreference()}>
      <FontAwesomeIcon className="mr-2" icon={["far", "envelope-open-text"]} />
      {t('Subscribe')}
    </ButtonNew>
  }</>;
}
